"use client";
import { Button, ButtonLink, Heading, ProductCard } from "@/components/atoms";
import Preamble from "@/components/atoms/Preamble/Preamble";
import { convertProductCardToCartItem } from "@/lib/centra/convertToCartItem/convertToCartItem";
import type { ProductCard as PC } from "@/lib/centra/formatters/formatProductCards/formatProductCard";
import { useCart } from "@/lib/hooks/useSelection";
import useAddToCart from "@/lib/hooks/useSelection/cart/useAddToCart";
import {
  useProductsInWishlist,
  wishlist,
  WishlistProduct,
} from "@/lib/wishlist/hooks";
import * as Tabs from "@radix-ui/react-tabs";
import clsx from "clsx";
import {
  Dispatch,
  SetStateAction,
  SVGProps,
  useCallback,
  useState,
} from "react";
import { useCopyToClipboard } from "usehooks-ts";
import { Input } from "../Form/FormInputField";
import styles from "./wishlist.module.css";

export const fetchPCByID = async (ids: string[]) => {
  const justVariant = ids?.flatMap(id => id?.split("-")[0]);

  const fetchIds = async (ids: string[]) => {
    const res = await fetch("/api/products/product-card", {
      method: "POST",
      body: JSON.stringify({
        ids,
      }),
    });
    return res.json();
  };

  const data = await fetchIds(justVariant);

  const pcs = data.filter((pc: any) => !!pc) as unknown as NonNullable<PC>[];

  const emptyObjectIds = ids.filter((id, i) => {
    const pc = pcs[i];
    return !pc || isEmptyObject(pc.description!);
  });

  const filteredPcs = pcs.filter(pc => {
    return !isEmptyObject(pc.description!);
  });

  const pcWithSize = filteredPcs.map((pc, i) => {
    const size = pc.description?.size?.find(s => s.itemId === ids[i]);
    return {
      ...pc,
      size,
    };
  });

  return { pcWithSize, emptyObjectIds };
};

export const isEmptyObject = (obj: object) =>
  Object.keys(obj).length === 0 && obj.constructor === Object;

const Wishlist = ({
  setTab,
}: {
  setTab: Dispatch<SetStateAction<"cart" | "wishlist">>;
}) => {
  const { wishlistId, remove } = wishlist.useWishlist();
  const { data } = useProductsInWishlist(wishlistId);

  const { addToCart } = useAddToCart();
  const { modal } = useCart();

  const move = (product: WishlistProduct) => {
    if (!product.size) return;

    remove(wishlist.toItemId(product.id!));

    addToCart.mutate(
      {
        itemId: product.size.itemId,
        cartItem: convertProductCardToCartItem(product, product.size),
      },
      {
        onSuccess: () => {
          setTab("cart");
        },
      },
    );
  };

  const share = useCallback(async () => {
    if (navigator.share) {
      await navigator.share({
        title: "My Wishlist",
        text: "Check out my wishlist!",
        url: `https://camillapihl.com/wishlist/${wishlistId}`,
      });
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(
        `https://camillapihl.com/wishlist/${wishlistId}`,
      );
      alert("Wishlist URL copied to clipboard!");
    } else {
      alert("Sharing not supported on this browser.");
    }
  }, []);

  if (!data?.length) {
    return (
      <Tabs.Content className={clsx(styles["cart-content"])} value="wishlist">
        <div className={styles["cart-empty"]}>
          <Preamble>Wishlist</Preamble>
          <Heading as="h3" styledAs="h4">
            Your wishlist is empty
          </Heading>
          <div className={styles.buttons}>
            <Button onClick={() => setTab("cart")} outlined>
              Go to cart
            </Button>
            <ButtonLink
              href="/products"
              color="black"
              textColor="white"
              onClick={modal.toggleModal}
            >
              Go shopping
            </ButtonLink>
          </div>
        </div>
      </Tabs.Content>
    );
  }

  return (
    <Tabs.Content
      className={styles.wishlistContainer}
      data-white-bg
      value="wishlist"
    >
      <div className={clsx(styles.container, styles.wishlistItemGrid)}>
        {data?.map((pc, i) => {
          const size = pc?.size;

          return (
            <div key={pc.id + "-pc-wl" + i}>
              <ProductCard key={pc.id} product={pc} inWishlist />
              {size && (
                <div className={styles.cardActions}>
                  {size && size.label !== "One Size" && (
                    <span className={styles.size}>Size: {size.label}</span>
                  )}

                  <button
                    className={styles.size}
                    onClick={() => {
                      move(pc);
                    }}
                  >
                    Move to cart
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ShareWishlist />
    </Tabs.Content>
  );
};

export default Wishlist;

export const ShareWishlist = ({ className }: { className?: string }) => {
  const [shouldShare, setShouldShare] = useState(false);

  return (
    <div
      className={clsx(
        styles.shareContainer,
        className,
        shouldShare && styles.open,
      )}
    >
      {shouldShare && (
        <ShareWishlistForm
          onCancel={() => {
            setShouldShare(false);
          }}
        />
      )}
      {!shouldShare && (
        <Button
          onClick={() => {
            setShouldShare(true);
          }}
          className={styles.button}
          outlined
        >
          SHARE WISHLIST
        </Button>
      )}
    </div>
  );
};

const ShareWishlistForm = ({ onCancel }: { onCancel: () => void }) => {
  const { wishlistId, share, data, isPending } = wishlist.useWishlist();

  const [copied, copy] = useCopyToClipboard();

  const url = `https://camillapihl.com/wishlist/${wishlistId}`;

  const shareToDevice = useCallback(async () => {
    if (navigator.share) {
      await navigator.share({
        title: `${data?.name || "My Wishlist"}`,
        text: "Check out my wishlist!",
        url: url,
      });
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(url);
    }
  }, []);

  const isShared = data?.name;

  return (
    <div style={{ width: "100%", maxWidth: "min(100%, 467px)" }}>
      <Heading
        style={{
          marginBottom: 18,
        }}
        as="h4"
        styledAs="h5"
      >
        Share your wishlist
      </Heading>

      <p className={styles.shareDesc}>
        Create a shareable link for your loved ones to view your wishlist.
      </p>

      <form
        onSubmit={e => {
          e.preventDefault();

          const displayName = (e.target as any).displayName.value;

          if (!displayName) return;

          share.mutate(displayName, {
            onSuccess: () => {
              shareToDevice();
            },
          });
        }}
        className={styles.shareForm}
      >
        {isShared && (
          <>
            <Input
              data-expanded
              label="Your sharable link"
              value={url}
              className={styles.readonlyShareInput}
              readOnly
            >
              <div className={styles.shareActions}>
                <button
                  onClick={() => {
                    copy(url);
                  }}
                  type="button"
                  className={styles.iconButton}
                >
                  {copied ? <LucideCheck /> : <LucideCopy />}
                </button>
                <button
                  onClick={shareToDevice}
                  type="button"
                  className={styles.iconButton}
                >
                  <MaterialSymbolsIosShareRounded />
                </button>
              </div>
            </Input>
            <Button
              type="button"
              textColor="white"
              color="black"
              onClick={onCancel}
            >
              DONE
            </Button>
          </>
        )}

        {!isShared && (
          <>
            <Input
              name="displayName"
              required
              label="Wishlist name"
              placeholder="e.g., Birthday Wishes, Holiday Favorites"
            />
            <Button type="submit" textColor="white" color="black">
              {isPending ? <EpLoading className={styles.animateSpin} /> : null}{" "}
              GENERATE WISHLIST LINK
            </Button>
            <Button
              onClick={onCancel}
              type="button"
              className={styles.button}
              outlined
            >
              CANCEL
            </Button>
          </>
        )}
      </form>
    </div>
  );
};

export function EpLoading(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 1024 1024"
      {...props}
    >
      {/* Icon from Element Plus by Element Plus - https://github.com/element-plus/element-plus-icons/blob/main/packages/svg/package.json */}
      <path
        fill="currentColor"
        d="M512 64a32 32 0 0 1 32 32v192a32 32 0 0 1-64 0V96a32 32 0 0 1 32-32m0 640a32 32 0 0 1 32 32v192a32 32 0 1 1-64 0V736a32 32 0 0 1 32-32m448-192a32 32 0 0 1-32 32H736a32 32 0 1 1 0-64h192a32 32 0 0 1 32 32m-640 0a32 32 0 0 1-32 32H96a32 32 0 0 1 0-64h192a32 32 0 0 1 32 32M195.2 195.2a32 32 0 0 1 45.248 0L376.32 331.008a32 32 0 0 1-45.248 45.248L195.2 240.448a32 32 0 0 1 0-45.248m452.544 452.544a32 32 0 0 1 45.248 0L828.8 783.552a32 32 0 0 1-45.248 45.248L647.744 692.992a32 32 0 0 1 0-45.248M828.8 195.264a32 32 0 0 1 0 45.184L692.992 376.32a32 32 0 0 1-45.248-45.248l135.808-135.808a32 32 0 0 1 45.248 0m-452.544 452.48a32 32 0 0 1 0 45.248L240.448 828.8a32 32 0 0 1-45.248-45.248l135.808-135.808a32 32 0 0 1 45.248 0"
      />
    </svg>
  );
}

export function MaterialSymbolsIosShareRounded(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      {...props}
    >
      {/* Icon from Material Symbols by Google - https://github.com/google/material-design-icons/blob/master/LICENSE */}
      <path
        fill="currentColor"
        d="M6 23q-.825 0-1.412-.587T4 21V10q0-.825.588-1.412T6 8h2q.425 0 .713.288T9 9t-.288.713T8 10H6v11h12V10h-2q-.425 0-.712-.288T15 9t.288-.712T16 8h2q.825 0 1.413.588T20 10v11q0 .825-.587 1.413T18 23zm5-18.175l-.9.9q-.3.3-.7.288T8.7 5.7q-.275-.3-.287-.7t.287-.7l2.6-2.6q.3-.3.7-.3t.7.3l2.6 2.6q.275.275.275.688T15.3 5.7q-.3.3-.712.3t-.713-.3L13 4.825V15q0 .425-.288.713T12 16t-.712-.288T11 15z"
      />
    </svg>
  );
}

export function LucideCopy(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      {...props}
    >
      {/* Icon from Lucide by Lucide Contributors - https://github.com/lucide-icons/lucide/blob/main/LICENSE */}
      <g
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      >
        <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
        <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
      </g>
    </svg>
  );
}

export function LucideCheck(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      {...props}
    >
      {/* Icon from Lucide by Lucide Contributors - https://github.com/lucide-icons/lucide/blob/main/LICENSE */}
      <path
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M20 6L9 17l-5-5"
      />
    </svg>
  );
}
